/* eslint-disable react/no-this-in-sfc */
import getXOrLastGroupNode from "components/charts/modern/helpers/getXOrLastGroupNode";
import classNames from "components/ui/classNames";
import type { TooltipFormatterContextObject } from "highcharts";
import type { ReactElement } from "react";
import { useMemo } from "react";
import type { IntlShape } from "react-intl";

interface QuotesChartTooltipProps {
  locale: string;
  context: TooltipFormatterContextObject;
  intl: IntlShape;
}
export default function QuotesChartTooltip({ locale, context, intl }: QuotesChartTooltipProps): ReactElement {
  const percentageFormatter = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      maximumFractionDigits: 1,
    });
  }, []);

  const moneyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: locale === "USA" ? "USD" : "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    minimumSignificantDigits: 2,
  });

  if (context.x && typeof context.x !== "string" && context.points && context.points.length > 0) {
    return (
      <div className="z-10 flex flex-col space-y-3 p-2 font-mono">
        <span className="inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 text-xs font-bold text-slate-600">
          {getXOrLastGroupNode(context, intl)}
        </span>

        <div className="space-y-1">
          {context.points
            .sort((a, b) => {
              return (b.y || 0) - (a.y || 0);
            })
            .map(({ y, series }) => {
              if (!y) {
                return null;
              }

              const color = String(series.options.custom?.primaryColor);
              const lineColor = String(series.options.custom?.secondaryColor);

              const lastY = series.points.length > 0 ? series.points[series.points.length - 1].y : null;
              const returnSoFar = lastY ? lastY / y - 1 : null;

              return (
                <div key={color} className="flex items-center justify-between space-x-3">
                  <span
                    className="rounded-md px-2 py-1  font-mono text-xs font-bold"
                    style={{
                      backgroundColor: color,
                      color: lineColor,
                    }}
                  >
                    {series.name}
                  </span>

                  <span className="font-mono font-semibold">{y ? moneyFormatter.format(y) : ""}</span>

                  {returnSoFar !== null && (
                    <span
                      className={classNames("font-mono font-semibold", {
                        "text-rose-600": returnSoFar < 0,
                        "text-slate-600": returnSoFar === 0,
                        "text-emerald-600": returnSoFar > 0,
                      })}
                    >
                      {percentageFormatter.format(returnSoFar)}
                    </span>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    );
  }

  return <span />;
}
